import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/* Web Speech API is exposed on window as either `SpeechRecognition` (Firefox
 * behind a flag) or `webkitSpeechRecognition` (Chrome / Edge / Safari). If
 * neither exists the mic button is hidden and the field behaves as a plain
 * text input. No audio is uploaded — recognition happens entirely in-browser. */
const getRecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const LANG_STORAGE_KEY = "vyomra-voice-lang";
const LANG_OPTIONS = [
  { code: "en-IN", label: "EN" },
  { code: "gu-IN", label: "ગુ" },
  { code: "hi-IN", label: "हि" },
];

/* Convert a spoken utterance into a compact digit string. Supports:
 *  - Pure digits from the recogniser ("9876543210", "42")
 *  - English digit words + Hindi/Gujarati transliterations (zero/shunya/ek…)
 *  - Teens & tens (ten…nineteen, twenty, thirty, twenty five → 25)
 *  - "double N"/"triple N" prefixes ("double nine three" → 993)
 *  - Punctuation, dashes, "at", "point" are ignored
 * Returns concatenated digits — caller is responsible for maxLength trimming.
 */
const WORD_DIGIT = {
  zero: "0", oh: "0", shunya: "0", sunya: "0",
  one: "1", ek: "1",
  two: "2", do: "2", be: "2", bay: "2",
  three: "3", teen: "3", tran: "3", tren: "3",
  four: "4", char: "4", chaar: "4", chaR: "4",
  five: "5", panch: "5", paanch: "5", paach: "5", panj: "5",
  six: "6", chhe: "6", chha: "6", chhah: "6", che: "6", chey: "6",
  seven: "7", saat: "7", saath: "7", sath: "7", saet: "7",
  eight: "8", aath: "8", aat: "8", ath: "8",
  nine: "9", nau: "9", nav: "9", nao: "9",
};
const TEENS = { ten: "10", eleven: "11", twelve: "12", thirteen: "13", fourteen: "14", fifteen: "15", sixteen: "16", seventeen: "17", eighteen: "18", nineteen: "19" };
const TENS = { twenty: "20", thirty: "30", forty: "40", fourty: "40", fifty: "50", sixty: "60", seventy: "70", eighty: "80", ninety: "90" };

export function speechToDigits(text) {
  if (!text) return "";
  const tokens = String(text).toLowerCase().replace(/[.,\-_/]/g, " ").split(/\s+/).filter(Boolean);
  let out = "";
  let mult = 1;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === "double") { mult = 2; continue; }
    if (t === "triple") { mult = 3; continue; }
    const raw = t.match(/\d+/g);
    if (raw) { out += raw.join("").repeat(mult); mult = 1; continue; }
    if (WORD_DIGIT[t] !== undefined) { out += WORD_DIGIT[t].repeat(mult); mult = 1; continue; }
    if (TEENS[t] !== undefined) { out += TEENS[t].repeat(mult); mult = 1; continue; }
    if (TENS[t] !== undefined) {
      const next = tokens[i + 1];
      if (next && WORD_DIGIT[next] !== undefined) {
        out += (parseInt(TENS[t], 10) + parseInt(WORD_DIGIT[next], 10)).toString();
        i++;
      } else {
        out += TENS[t];
      }
      mult = 1; continue;
    }
    if (t === "hundred") { out += "00"; mult = 1; continue; }
    mult = 1; // reset multiplier on unknown token
  }
  return out;
}

export function useSpeechRecognition({ lang = "en-IN", onFinal, onInterim, onError }) {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => !!getRecognitionCtor());
  const ref = useRef(null);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      onError && onError("Voice input not supported in this browser. Use Chrome / Edge / Safari.");
      return;
    }
    if (ref.current) {
      try { ref.current.stop(); } catch { /* noop */ }
      ref.current = null;
    }
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final && onFinal) onFinal(final.trim());
      if (interim && onInterim) onInterim(interim.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      const err = e?.error || "unknown";
      if (err === "no-speech") return; // benign
      onError && onError(err === "not-allowed"
        ? "Microphone permission denied. Enable it in your browser and reload."
        : `Voice error: ${err}`);
    };

    try {
      rec.start();
      ref.current = rec;
      setListening(true);
    } catch (err) {
      onError && onError(String(err?.message || err));
    }
  }, [lang, onFinal, onInterim, onError]);

  const stop = useCallback(() => {
    if (ref.current) {
      try { ref.current.stop(); } catch { /* noop */ }
      ref.current = null;
    }
    setListening(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { supported, listening, start, stop };
}

/* Reusable dictation-enabled wrapper.
 *   as="textarea" | "input" (default textarea)
 *   value / onChange behave like normal controlled inputs.
 *   Final results are appended at current cursor position (with a leading
 *   space when needed). Interim results are shown as a subtle badge below the
 *   field but never mutate value — the operator only ever edits the final
 *   text, matching the "allow editing before saving" requirement. */
export default function VoiceInput({
  as = "textarea",
  value = "",
  onChange,
  placeholder,
  rows = 3,
  className = "",
  testid,
  disabled = false,
  autoFocus,
  numeric = false,
  maxLength,
  ...rest
}) {
  const [lang, setLang] = useState("en-IN");
  const [interim, setInterim] = useState("");
  const fieldRef = useRef(null);

  // English is the default. We no longer persist across sessions so the pill
  // always resets to EN on reload; users can still switch per-session.
  useEffect(() => {
    try { localStorage.removeItem(LANG_STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const insertAtCursor = useCallback((text) => {
    // Numeric mode: transform speech → digits and append (no spaces, respect maxLength).
    if (numeric) {
      const digits = speechToDigits(text);
      if (!digits) return;
      const currentDigits = String(value || "").replace(/\D/g, "");
      let next = currentDigits + digits;
      if (maxLength && next.length > maxLength) next = next.slice(0, maxLength);
      onChange && onChange(next);
      return;
    }
    const el = fieldRef.current;
    if (!el) {
      onChange && onChange((value || "") + (value ? " " : "") + text);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const needsLead = before && !/\s$/.test(before);
    const needsTail = after && !/^\s/.test(after);
    const inserted = (needsLead ? " " : "") + text + (needsTail ? " " : "");
    let next = before + inserted + after;
    if (maxLength && next.length > maxLength) next = next.slice(0, maxLength);
    onChange && onChange(next);
    // Restore cursor just after inserted text
    requestAnimationFrame(() => {
      if (fieldRef.current) {
        const pos = (before + inserted).length;
        fieldRef.current.setSelectionRange(pos, pos);
        fieldRef.current.focus();
      }
    });
  }, [value, onChange, numeric, maxLength]);

  const { supported, listening, start, stop } = useSpeechRecognition({
    lang,
    onFinal: (t) => { setInterim(""); insertAtCursor(t); },
    onInterim: setInterim,
    onError: (m) => { setInterim(""); toast.error(m); },
  });

  const toggle = () => (listening ? stop() : start());

  const FieldEl = as === "input" ? Input : Textarea;
  const fieldProps = as === "input" ? {} : { rows };
  if (numeric) {
    fieldProps.inputMode = "numeric";
    fieldProps.pattern = "[0-9]*";
    if (maxLength) fieldProps.maxLength = maxLength;
  } else if (maxLength) {
    fieldProps.maxLength = maxLength;
  }

  return (
    <div className={`relative ${className}`}>
      <FieldEl
        ref={fieldRef}
        value={value}
        onChange={(e) => {
          let v = e.target.value;
          if (numeric) v = v.replace(/\D/g, "");
          if (maxLength && v.length > maxLength) v = v.slice(0, maxLength);
          onChange && onChange(v);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testid}
        className={`pr-20 ${as === "textarea" ? "min-h-[60px]" : ""}`}
        {...fieldProps}
        {...rest}
      />

      {/* Mic + language pill */}
      {supported && (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            disabled={listening}
            data-testid={testid ? `${testid}-lang` : undefined}
            className="h-6 text-[10px] border border-slate-200 bg-white rounded-sm px-1 hover:border-blue-400"
            title="Voice recognition language"
          >
            {LANG_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
          <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            data-testid={testid ? `${testid}-mic` : undefined}
            title={listening ? "Stop recording" : "Start voice dictation"}
            aria-label={listening ? "Stop recording" : "Start voice dictation"}
            className={`h-6 w-6 flex items-center justify-center border rounded-sm transition ${
              listening
                ? "bg-red-600 border-red-700 text-white animate-pulse"
                : "bg-white border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-700"
            }`}
          >
            {listening ? <MicOff className="w-3.5 h-3.5"/> : <Mic className="w-3.5 h-3.5"/>}
          </button>
        </div>
      )}

      {/* Live interim transcript hint */}
      {listening && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
          <Loader2 className="w-3 h-3 animate-spin text-red-600"/>
          <span className="italic">Listening ({lang})…</span>
          {interim && <span className="text-slate-700 truncate max-w-[280px]">· “{interim}”</span>}
        </div>
      )}
    </div>
  );
}
